import { inject } from '@angular/core';
import { RedirectFunction, Routes } from '@angular/router';
import { TranslationService } from './services/translation.service';
import { TestComponent } from './test/test.component';
import { LoginComponent } from './login/login.component';
import { MainComponent } from './main/main.component';
import { SymptomsComponent } from './symptoms/symptoms.component';
import { InitialResultsComponent } from './initial-results/initial-results.component';
import { FullResultsComponent } from './full-results/full-results.component';
import { FullResultsSymptomsComponent } from './full-results-symptoms/full-results-symptoms.component';
import { DeleteUserComponent } from './delete-user/delete-user.component';
import { UserAccountComponent } from './user-account/user-account.component';
import { PaymentCodeGuard } from './guards/payment-code.guard';
import { SpinnerComponent } from './shared/components/spinner/spinner.component';
import { PurposeComponent } from './purpose/purpose.component';
import { HomeComponent } from './shell/home/home.component';
import { ToolsCatalogueComponent } from './shell/tools-catalogue/tools-catalogue.component';
import { ToolHostComponent } from './shell/tool-host/tool-host.component';
import { AppOnboardingComponent } from './shell/onboarding/app-onboarding.component';
import { NewTestComponent } from './new-test/new-test.component';
import { CognitiveComponent } from './cognitive/cognitive.component';
import { SenseComponent } from './sense/sense.component';
import { WelcomeComponent } from './welcome/welcome.component';
import { ResultsComponent } from './results/results.component';
import { SignupComponent } from './signup/signup.component';
import { ResetPasswordComponent } from './reset-password/reset-password.component';
import { ConfirmEmailComponent } from './confirm-email/confirm-email.component';
import { languageGuard } from './guards/language.guard';
import { authGuard } from './guards/auth.guard';
import { guestGuard } from './guards/guest.guard';
import { webDailyLoopGuard } from './guards/web-daily-loop.guard';
import { clinicLoginGuard } from './guards/clinic-login.guard';
import { testInProgressGuard } from './guards/test-in-progress.guard';
import { toolsAccessGuard } from './guards/tools-access.guard';
import { TrialResultComponent } from './trial-result/trial-result.component';
import { PaymentComponent } from './payment/payment.component';
import { PaymentSuccessComponent } from './payment/payment-success/payment-success.component';
import { DoctorReportComponent } from './doctor-report/doctor-report.component';
import { ReportViewComponent } from './doctor-report/report-view/report-view.component';
import { DailyCheckinComponent } from './daily-checkin/daily-checkin.component';
import { MaccabiPortalComponent } from './maccabi-portal/maccabi-portal.component';
import { PatientPortalComponent } from './patient-portal/patient-portal.component';

const welcomeRedirect = () =>
  `/${inject(TranslationService).getCurrentLanguage()}/app/welcome`;

// Bare /maccabi convenience alias. Preserve the visitor's locale instead of
// hardcoding /he, so an English link keeps English through to the portal.
const maccabiRedirect = () =>
  `/${inject(TranslationService).getCurrentLanguage()}/app/doctor/maccabi`;

// Short, org-less patient invitation link: markable.life/i/<token>. The token
// carries clinic/doctor/expiry, so the URL has no org name and no locale segment.
// The sending doctor stamps her portal language as ?lang=he|en so a fresh
// (not-logged-in) patient opens the approve flow in the portal's language rather
// than the Markable Hebrew default. A logged-in patient's own saved language
// still wins (resolveInitialPickerLanguage), so ?lang only steers first visits.
const inviteRedirect: RedirectFunction = (r) => {
  const stamped = r.queryParams['lang'];
  const lang =
    stamped === 'he' || stamped === 'en'
      ? stamped
      : inject(TranslationService).getCurrentLanguage();
  // ClinicJoinComponent resolves the inviting doctor's clinic and sends the
  // patient to the branded /login/<slug>?invite= page (or the legacy approve
  // screen for a solo doctor).
  return `/${lang}/invite/${r.params['token']}`;
};

// Legacy Maccabi doctor-portal URLs (saved in doctors' mail/bookmarks) keep
// working by redirecting to the generic /app/doctor/* routes, locale preserved.
// The landing carries ?org=maccabi so it co-brands; authed URLs stay org-less.
const portalLang = () => inject(TranslationService).getCurrentLanguage();
const maccabiPatientsRedirect: RedirectFunction = () =>
  `/${portalLang()}/app/doctor/patients`;
const maccabiApproveRedirect: RedirectFunction = (r) =>
  `/${portalLang()}/app/doctor/approve/${r.params['token']}`;
const maccabiReportRedirect: RedirectFunction = (r) =>
  `/${portalLang()}/app/doctor/report/${r.params['scanId']}`;

export const routes: Routes = [
  // The one short link behind the app-download QR. Top-level (no :lang), so the
  // printed/scanned URL stays short; the component picks the store by device.
  {
    path: 'get',
    loadComponent: () =>
      import('./shared/components/app-download/get-app-redirect.component').then(
        (m) => m.GetAppRedirectComponent,
      ),
  },
  { path: 'maccabi', redirectTo: maccabiRedirect, pathMatch: 'full' },
  { path: 'i/:token', redirectTo: inviteRedirect, pathMatch: 'full' },
  {
    path: ':lang',
    canActivate: [languageGuard],
    children: [
      // Generic doctor portal (default, clean MARKABLE, no org in the URL).
      { path: 'app/doctor', component: MaccabiPortalComponent },
      { path: 'app/doctor/login', component: MaccabiPortalComponent },
      { path: 'app/doctor/setup', component: MaccabiPortalComponent },
      { path: 'app/doctor/patients', component: MaccabiPortalComponent },
      { path: 'app/doctor/approve/:token', component: MaccabiPortalComponent },
      { path: 'app/doctor/report/:scanId', component: ReportViewComponent },
      // Clinic-branded doctor landing (login / signup / member invite):
      // /app/doctor/<clinic slug>. Declared after the fixed segments above so
      // login|setup|patients never resolve as a slug.
      { path: 'app/doctor/:slug', component: MaccabiPortalComponent },
      {
        // Patient entry portal (reached from the doctor's SMS invite). Public,
        // no auth guard: she may land here before logging in. Kept out of
        // 'app/doctor' so it can serve other partner bodies in future; Maccabi
        // branding lives inside the page.
        path: 'app/patient',
        component: PatientPortalComponent,
      },
      {
        path: 'app/patient/maccabi',
        component: PatientPortalComponent,
      },
      {
        path: 'app/patient/consent',
        component: PatientPortalComponent,
      },
      {
        path: 'app/patient/maccabi/consent',
        component: PatientPortalComponent,
      },
      {
        path: 'app/doctor/maccabi/patients',
        redirectTo: maccabiPatientsRedirect,
        pathMatch: 'full',
      },
      {
        path: 'app/doctor/maccabi/approve/:token',
        redirectTo: maccabiApproveRedirect,
        pathMatch: 'full',
      },
      {
        path: 'app/doctor/maccabi/report/:scanId',
        redirectTo: maccabiReportRedirect,
        pathMatch: 'full',
      },
      {
        // QA render-only preview of the provider report from a fake fixture payload
        // (?fixture=worst_case|best_case|first_scan|sparse_history|partial_cognitive).
        // Public, no DB, no auth - lets QA eyeball the report's edge cases.
        path: 'provider-report/preview',
        component: ReportViewComponent,
      },
      {
        // The headless renderer's way in: the server opens this route with a
        // short-lived print token (?t=) to turn the report into a PDF for the
        // browsers that never open a print dialog. No guard by design - the
        // token is the authorisation, and it only unlocks that one report.
        path: 'report-print/:scanId',
        component: ReportViewComponent,
      },
      {
        // QA render-only preview of the 100-item symptom sweep (mark-then-rate
        // flow, fixes/04_UX_SPEC). Public, no auth, NOT wired into the live
        // funnel - lets Danna review the new flow before the atomic go-live.
        path: 'symptoms-sweep/preview',
        loadComponent: () =>
          import('./symptom-sweep/symptom-sweep.component').then(
            (m) => m.SymptomSweepComponent,
          ),
      },
      {
        // QA render-only preview of the SHARP short cognitive battery (handoff
        // 2026-08-17). Public, no auth, no scan/DB write - lets Danna walk the
        // five-stage flow before the go-live. Finishing loops back to the intro.
        path: 'cognitive/preview',
        component: CognitiveComponent,
        data: { preview: true },
      },
      {
        // QA render-only preview of the SENSE v2 sensory check (vision contrast +
        // tone-in-noise, handoff 2026-08-17). Public, no auth, no scan/DB write -
        // lets Danna walk the two checks before go-live. Finishing re-runs.
        path: 'sense/preview',
        component: SenseComponent,
        data: { preview: true },
      },
      {
        // QA render-only preview of the SENSE RESULTS CARD (vision+hearing, no
        // score/band). Public, no auth. Renders every state via the real VM
        // builder (first scan, change, stable, not-measured, trend gaps). 03 sec Z.
        path: 'sense-results/preview',
        loadComponent: () =>
          import('./results/panels/sense/sense-preview.component').then(
            (m) => m.SensePreviewComponent
          ),
      },
      {
        // Patient invite landing / clinic join (consent + attach). Public: she
        // may arrive logged out; the component sends her through the branded
        // login and the spinner settles the pending join after auth.
        path: 'invite/:token',
        loadComponent: () =>
          import('./clinic/clinic-join/clinic-join.component').then(
            (m) => m.ClinicJoinComponent,
          ),
      },
      {
        path: 'join/:slug',
        loadComponent: () =>
          import('./clinic/clinic-join/clinic-join.component').then(
            (m) => m.ClinicJoinComponent,
          ),
      },
      {
        // Create a clinic (any signed-in user; the creator becomes its admin).
        path: 'clinic/new',
        loadComponent: () =>
          import('./clinic/clinic-new/clinic-new.component').then(
            (m) => m.ClinicNewComponent,
          ),
        canActivate: [authGuard],
      },
      {
        // Clinic admin (clinic admins + system admins; the API enforces role).
        path: 'clinic/:slug/admin',
        loadComponent: () =>
          import('./clinic/clinic-admin/clinic-admin.component').then(
            (m) => m.ClinicAdminComponent,
          ),
        canActivate: [authGuard],
      },
      {
        path: 'app',
        component: MainComponent,
        canActivate: [PaymentCodeGuard],
        children: [
          {
            path: 'welcome',
            component: WelcomeComponent,
            canActivate: [guestGuard],
          },
          {
            path: 'delete',
            component: DeleteUserComponent,
          },
          {
            path: 'onboarding',
            component: AppOnboardingComponent,
            canActivate: [authGuard],
            data: { bare: true },
          },
          {
            path: 'home',
            component: HomeComponent,
            canActivate: [authGuard, webDailyLoopGuard],
            data: { shell: true },
          },
          {
            path: 'tools',
            component: ToolsCatalogueComponent,
            canActivate: [authGuard, webDailyLoopGuard, toolsAccessGuard],
            data: { shell: true },
          },
          {
            path: 'tool/:id',
            component: ToolHostComponent,
            canActivate: [authGuard, webDailyLoopGuard, toolsAccessGuard],
            // F20: the tool screens keep the bottom nav on screen while a check
            // or exercise runs, so a nav tap has to confirm before it discards it.
            canDeactivate: [testInProgressGuard],
            data: { shell: true },
          },
          {
            path: 'purpose',
            component: PurposeComponent,
          },
          {
            path: 'new-test',
            component: NewTestComponent,
          },
          {
            // Go-live (staging): the symptom-rating step is now the 100-item
            // mark-then-rate sweep, replacing the legacy 60-item SymptomsFormComponent.
            // Same route + scanId, so the funnel navigation is unchanged.
            path: 'symptoms-form/:scanId',
            // F20: the sweep holds every rating in memory until the finish CTA.
            canDeactivate: [testInProgressGuard],
            loadComponent: () =>
              import('./symptom-sweep/symptom-sweep.component').then(
                (m) => m.SymptomSweepComponent,
              ),
          },
          {
            path: 'cognitive/:scanId',
            component: CognitiveComponent,
            canDeactivate: [testInProgressGuard],
          },
          {
            path: 'sense/:scanId',
            component: SenseComponent,
            canDeactivate: [testInProgressGuard],
          },
          {
            path: 'results/:scanId',
            component: ResultsComponent,
            canActivate: [authGuard],
          },
          {
            path: 'trial-result/:scanId',
            component: TrialResultComponent,
            canActivate: [authGuard],
          },
          {
            path: 'payment',
            component: PaymentComponent,
            canActivate: [authGuard],
          },
          {
            path: 'payment-success',
            component: PaymentSuccessComponent,
          },
          {
            path: 'test',
            component: TestComponent,
          },
          {
            path: 'symptoms/:scanId',
            component: SymptomsComponent,
            // F20: unsaved intake answers - confirm before leaving the flow.
            canDeactivate: [testInProgressGuard],
          },
          {
            path: 'initial-results/:scanId',
            component: InitialResultsComponent,
          },
          {
            path: 'user-account',
            component: UserAccountComponent,
            canActivate: [authGuard],
            data: { shell: true },
          },
          {
            path: 'widget-settings',
            loadComponent: () =>
              import('./shell/widget-settings/widget-settings.component').then(
                (m) => m.WidgetSettingsComponent,
              ),
            canActivate: [authGuard],
            data: { shell: true },
          },
          {
            path: 'daily-checkin',
            component: DailyCheckinComponent,
            canActivate: [authGuard, webDailyLoopGuard],
          },
          {
            path: 'doctor-report/:scanId',
            component: DoctorReportComponent,
            canActivate: [authGuard],
          },
          {
            path: 'doctor-report/:scanId/view',
            component: ReportViewComponent,
            canActivate: [authGuard],
          },
          {
            path: 'full-results',
            canActivate: [authGuard],
            children: [
              {
                path: ':scanId',
                component: FullResultsComponent,
              },
              {
                path: ':scanId/symptoms',
                component: FullResultsSymptomsComponent,
              },
            ],
          },
          {
            path: 'loading',
            component: SpinnerComponent,
          },
          { path: '', redirectTo: 'welcome', pathMatch: 'full' },
        ],
      },
      {
        path: 'admin',
        loadChildren: () =>
          import('./admin/admin.routes').then((m) => m.ADMIN_ROUTES),
        canActivate: [authGuard],
      },
      {
        path: 'maccabi',
        redirectTo: 'app/doctor/maccabi',
        pathMatch: 'full',
      },
      {
        path: 'maccabi/patients',
        redirectTo: 'app/doctor/maccabi/patients',
        pathMatch: 'full',
      },
      {
        path: 'maccabi/approve/:token',
        redirectTo: 'app/doctor/maccabi/approve/:token',
      },
      {
        path: 'maccabi/report/:scanId',
        redirectTo: 'app/doctor/maccabi/report/:scanId',
      },
      {
        path: 'doctor/maccabi',
        redirectTo: 'app/doctor/maccabi',
        pathMatch: 'full',
      },
      {
        path: 'doctor/maccabi/patients',
        redirectTo: 'app/doctor/maccabi/patients',
        pathMatch: 'full',
      },
      {
        path: 'doctor/maccabi/approve/:token',
        redirectTo: 'app/doctor/maccabi/approve/:token',
      },
      {
        path: 'doctor/maccabi/report/:scanId',
        redirectTo: 'app/doctor/maccabi/report/:scanId',
      },
      {
        path: 'preview-tools',
        loadComponent: () =>
          import('./preview-tools.component').then((m) => m.PreviewToolsComponent),
      },
      {
        path: 'login',
        component: LoginComponent,
        canActivate: [guestGuard, PaymentCodeGuard],
      },
      {
        path: 'signup',
        component: SignupComponent,
        canActivate: [guestGuard, PaymentCodeGuard],
      },
      {
        // White-labelled clinic login: /:lang/login/<clinic slug>[?invite=].
        path: 'login/:clinic',
        component: LoginComponent,
        canActivate: [clinicLoginGuard, PaymentCodeGuard],
      },
      {
        path: 'signup/:clinic',
        component: SignupComponent,
        canActivate: [clinicLoginGuard, PaymentCodeGuard],
      },
      {
        // Password-reset landing from the recovery email. NO guestGuard: the
        // recovery link establishes a Supabase session, so the user is
        // "authenticated" here — guestGuard would bounce them to home. The
        // component drives navigation after the new password is set.
        path: 'reset-password',
        component: ResetPasswordComponent,
      },
      {
        // Signup-confirmation landing from the email link. NO guestGuard, same
        // reasoning as reset-password: the link may establish a session (or
        // arrive with a token_hash the component verifies itself).
        path: 'confirm-email',
        component: ConfirmEmailComponent,
      },
      // Same page for signups started in the native app: hands the token to the app.
      // A path (not ?app=1) because the email template appends "?token_hash=".
      { path: 'confirm-email-app', component: ConfirmEmailComponent },
      { path: '', redirectTo: 'app/welcome', pathMatch: 'full' },
    ],
  },
  // Root + wildcard land on welcome in the user's RESOLVED language. We can't
  // hardcode a language here: the JS bundle is shared by both brands, and
  // Markable defaults to Hebrew while Peritale defaults to English (resolved in
  // TranslationService: saved choice -> URL -> brand default). A static
  // '/en/...' redirect forced /en, and languageGuard then persisted 'en' on a
  // fresh Markable install, defeating the Hebrew brand default. The functional
  // redirect runs in an injection context, so getCurrentLanguage() is correct.
  { path: '', pathMatch: 'full', redirectTo: welcomeRedirect },
  { path: '**', redirectTo: welcomeRedirect },
];
